import React, { useEffect, useRef, useState } from 'react';
import { TextField, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, MenuItem, Button, Grid, Paper, Typography, Collapse, Autocomplete } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from '../../contexts/AuthContext';
import 'dayjs/locale/en-gb';
import api from '../../api/api';
import { useNavigate } from 'react-router-dom';

export default function CarRequestForm() {

  const navigate = useNavigate();

  const { user, token } = useAuth();

  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [formValues, setFormValues] = useState({
    empId: '',
    empName: '',
    requestFor: 'self',
    forEmployeeId: null,
    forGuestName: '',
    tentativeKm: '',
    fromLocation: '',
    toLocation: '',
    date: '',
    departureTime: '',
    arrivalTime: '',
    numberOfPersons: '',
    purpose: '',
    approvalManager: ''
  });

  const fromLocationRef = useRef(null);
  const toLocationRef = useRef(null);
  const fromLocationAutocomplete = useRef(null);
  const toLocationAutocomplete = useRef(null);

  useEffect(() => {
    fromLocationAutocomplete.current = new window.google.maps.places.Autocomplete(fromLocationRef.current);
    toLocationAutocomplete.current = new window.google.maps.places.Autocomplete(toLocationRef.current);

    fromLocationAutocomplete.current.addListener('place_changed', () => {
      const place = fromLocationAutocomplete.current.getPlace();
      setFormValues((prevValues) => ({
        ...prevValues,
        fromLocation: place.name + ", " + place.formatted_address,
      }));
      calculateDistance();
    });

    toLocationAutocomplete.current.addListener('place_changed', () => {
      const place = toLocationAutocomplete.current.getPlace();
      setFormValues((prevValues) => ({
        ...prevValues,
        toLocation: place.name + ", " + place.formatted_address,
      }));
      calculateDistance();
    });

    api.get('/api/Users/get_managers')
      .then(response => {
        setManagers(response.data);
      })
      .catch(error => {
        console.error('Error fetching managers', error);
      });

    api.get('/api/Employees')
      .then(response => {
        setEmployees(response.data);
      })
      .catch(error => {
        console.error('Error fetching managers', error);
      });

  }, []);

  const handleDateChange = (date) => {
    setFormValues({ ...formValues, date });
  };

  const handleTimeChange = (time, name) => {
    setFormValues({ ...formValues, [name]: time });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    if (formValues.requestFor !== 'guest') {
      setFormValues((prevValues) => ({
        ...prevValues,
        forGuestName: '',
      }));
    }

    if (formValues.requestFor !== 'employee') {
      setFormValues((prevValues) => ({
        ...prevValues,
        forEmployeeId: null,
      }));
    }
  };

  const calculateDistance = () => {
    if (!fromLocationAutocomplete.current.getPlace() || !toLocationAutocomplete.current.getPlace()) return;

    const origin = fromLocationAutocomplete.current.getPlace().formatted_address;
    const destination = toLocationAutocomplete.current.getPlace().formatted_address;

    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === 'OK') {
          const distanceValue = response.rows[0].elements[0].distance.value / 1000; // Convert meters to kilometers
          setFormValues((prevValues) => ({
            ...prevValues,
            tentativeKm: Math.round(distanceValue),
          }));
        } else {
          console.error(`Error calculating distance: ${status}`);
        }
      }
    );
  };

  useEffect(() => {
    if (user) {
      const tempValues = { ...formValues }
      tempValues.empId = user.employee_code
      tempValues.empName = user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
      setFormValues(tempValues)
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Call the POST /api/CarRequests API
    api.post('/api/CarRequests', {
      employeeId: user["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
      approvalManagerId: formValues.approvalManager,
      requestFor: formValues.requestFor === 'self' ? 0 : formValues.requestFor === 'employee' ? 1 : 2,
      forGuestName: formValues.forGuestName,
      forEmployeeId: formValues.forEmployeeId,
      tentativeKm: formValues.tentativeKm,
      fromLocation: formValues.fromLocation,
      toLocation: formValues.toLocation,
      journeyDate: formValues.date,
      departureTime: formValues.departureTime,
      arrivalTime: formValues.arrivalTime,
      noOfPerson: formValues.numberOfPersons,
      purposeOfRequirement: formValues.purpose,
    })
      .then(response => {
        navigate('/car-request')
      })
      .catch(error => {
        console.error('Error submitting request', error);
        // Handle error
      });
  };

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 1200,
        margin: 'auto'
      }}
    >
      <Typography marginBottom={2} component="p" variant="h5">New Car Request</Typography>
      <form sx={{ top: 200 }} onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Emp. ID"
              name="empId"
              value={formValues.empId}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Emp Name"
              name="empName"
              value={formValues.empName}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Request for</FormLabel>
              <RadioGroup
                row
                name="requestFor"
                value={formValues.requestFor}
                onChange={handleInputChange}
              >
                <FormControlLabel value="self" control={<Radio />} label="Self" />
                <FormControlLabel value="employee" control={<Radio />} label="Employee" />
                <FormControlLabel value="guest" control={<Radio />} label="Guest" />
              </RadioGroup>
            </FormControl>
          </Grid>
          {
            formValues.requestFor === 'guest' ?
              <Grid item xs={12} sm={6} alignContent={'center'} style={{ paddingTop: formValues.requestFor === 'guest' ? undefined : 0 }}>
                <Collapse fullWidth in={formValues.requestFor === 'guest'}>
                  <Grid item>
                    <TextField
                      label="Guest Name"
                      name="forGuestName"
                      value={formValues.forGuestName}
                      onChange={handleInputChange}
                      fullWidth
                      inputProps={{ required: formValues.requestFor === 'guest' }}
                    />
                  </Grid>
                </Collapse>
              </Grid>
              :
              <Grid item xs={12} sm={6} alignContent={'center'} style={{ paddingTop: formValues.requestFor === 'employee' ? undefined : 0 }}>
                <Collapse fullWidth in={formValues.requestFor === 'employee'}>
                  <Grid item>
                    <Autocomplete
                      disablePortal
                      options={employees}
                      getOptionLabel={(employee) => `${employee.name} (${employee.employeeCode})`}
                      getOptionKey={(employee) => employee.id}
                      onChange={(event, value) => setFormValues((prevValues) => ({
                        ...prevValues,
                        forEmployeeId: value?.id || null
                      }))}
                      value={employees.find(employee => employee.id === formValues.forEmployeeId) || null}
                      sx={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="For Employee Name"
                          required={formValues.requestFor === 'employee'}
                        />
                      )}
                    />
                  </Grid>
                </Collapse>
              </Grid>
          }
          <Grid item xs={12} sm={6}>
            <TextField
              label="From Location"
              name="fromLocation"
              value={formValues.fromLocation}
              onChange={handleInputChange}
              fullWidth
              inputRef={fromLocationRef}
              inputProps={{ required: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="To Location"
              name="toLocation"
              value={formValues.toLocation}
              onChange={handleInputChange}
              fullWidth
              inputRef={toLocationRef}
              inputProps={{ required: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Tentative KM"
              name="tentativeKm"
              type="number"
              value={formValues.tentativeKm}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="No. of persons"
              name="numberOfPersons"
              type="number"
              inputProps={{ min: 1, required: true }}
              onWheel={(e) => e.target.blur()}
              value={formValues.numberOfPersons}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Grid item xs={12} sm={12} xl={4}>
              <DatePicker sx={{ width: '100%' }} disablePast inputProps={{ required: true }} onChange={(date) => handleDateChange(date)} label="Date" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} xl={4}>
              <TimePicker sx={{ width: '100%' }} inputProps={{ required: true }} onChange={(time) => handleTimeChange(time, 'departureTime')} label="Departure Time" ampm fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} xl={4}>
              <TimePicker sx={{ width: '100%' }} inputProps={{ required: true }} onChange={(time) => handleTimeChange(time, 'arrivalTime')} label="Arrival Time" ampm fullWidth />
            </Grid>
          </LocalizationProvider>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Purpose of requirement"
              name="purpose"
              value={formValues.purpose}
              onChange={handleInputChange}
              inputProps={{ required: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              disablePortal
              options={managers}
              getOptionLabel={(manager) => manager.name}
              getOptionKey={(manager) => manager.id}
              onChange={(event, value) => setFormValues((prevValues) => ({
                ...prevValues,
                approvalManager: value?.id || ''
              }))}
              value={managers.find(manager => manager.id === formValues.approvalManager) || null}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Approval Manager"
                  required
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}
